import { motion } from "framer-motion";
import { ArrowUpRight, Quote } from "lucide-react";

const LINKEDIN_RECS = "https://www.linkedin.com/in/himanshukumaroberoi/details/recommendations/";

const EASE = [0.22, 1, 0.36, 1];
const VIEWPORT = { once: true, margin: "-80px" };
const FADE_20 = { opacity: 0, y: 20 };
const FADE_30 = { opacity: 0, y: 30 };
const VISIBLE = { opacity: 1, y: 0 };
const QUOTE_INITIAL = { opacity: 0, scale: 0.8 };
const QUOTE_VISIBLE = { opacity: 1, scale: 1 };
const SLOW_EASE_TRANSITION = { duration: 1, ease: EASE };
const LABEL_TRANSITION = { duration: 0.9 };
const SUB_TRANSITION = { duration: 0.9, delay: 0.15 };
const CTA_TRANSITION = { duration: 0.9, delay: 0.25 };
const HAIRLINE_INITIAL = { opacity: 0, scaleX: 0 };
const HAIRLINE_VISIBLE = { opacity: 1, scaleX: 1 };
const HAIRLINE_TRANSITION = { duration: 1.2, ease: EASE };

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      className="relative container-x py-24 md:py-40"
      data-testid="testimonials-section"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
        <div className="col-span-12 md:col-span-4">
          <p className="eyebrow">Testimonials</p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <motion.p
            initial={FADE_20}
            whileInView={VISIBLE}
            viewport={VIEWPORT}
            transition={LABEL_TRANSITION}
            className="font-mono text-xs tracking-[0.28em] uppercase text-steel"
          >
            — Recommendations on LinkedIn
          </motion.p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
        <div className="col-span-12 md:col-span-1 hidden md:block">
          <motion.div
            initial={QUOTE_INITIAL}
            whileInView={QUOTE_VISIBLE}
            viewport={VIEWPORT}
            transition={SLOW_EASE_TRANSITION}
          >
            <Quote size={64} strokeWidth={1} className="text-laser/70 -scale-x-100" />
          </motion.div>
        </div>

        <div className="col-span-12 md:col-span-8">
          <motion.h2
            initial={FADE_30}
            whileInView={VISIBLE}
            viewport={VIEWPORT}
            transition={SLOW_EASE_TRANSITION}
            className="font-serif font-light text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink"
            data-testid="testimonials-title"
          >
            Performance validated not just in numbers, but in <span className="text-laser italic">words.</span>
          </motion.h2>

          <motion.p
            initial={FADE_20}
            whileInView={VISIBLE}
            viewport={VIEWPORT}
            transition={SUB_TRANSITION}
            className="mt-6 font-sans text-lg text-steel max-w-2xl leading-relaxed"
          >
            Colleagues, clients and partners have taken the time to write their reflections on the work.
            Read them, verified, on LinkedIn.
          </motion.p>
        </div>

        <div className="col-span-12 md:col-span-3 md:justify-self-end">
          <motion.a
            href={LINKEDIN_RECS}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="testimonials-cta"
            initial={FADE_20}
            whileInView={VISIBLE}
            viewport={VIEWPORT}
            transition={CTA_TRANSITION}
            className="btn-sweep btn-sweep-primary group inline-flex items-center gap-3 border border-laser text-ink px-6 py-4 font-mono text-xs tracking-[0.24em] uppercase"
          >
            Read on LinkedIn
            <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </motion.a>
        </div>
      </div>

      <motion.div
        initial={HAIRLINE_INITIAL}
        whileInView={HAIRLINE_VISIBLE}
        viewport={VIEWPORT}
        transition={HAIRLINE_TRANSITION}
        className="mt-16 md:mt-20 origin-left hairline"
      />
    </section>
  );
}
